"use strict";
/**
 * Runs a catalogue connector's hook (HOOK-PROTOCOL.md v1): the `initiate` HTTP
 * step, then the `open` URL to embed or redirect to, or the best-effort
 * `disconnect` step. Shared by the HDS webapp and the account app's `/connect`.
 *
 * Pure-function discipline: no DOM, no UI, no side effects beyond the configured
 * HTTP steps. The caller decides how to act on the returned mode.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.HookInitiateError = exports.UnresolvedVariableError = void 0;
exports.expand = expand;
exports.executeHook = executeHook;
exports.executeDisconnect = executeDisconnect;
const logger = __importStar(require("../logger.js"));
class UnresolvedVariableError extends Error {
    variable;
    constructor(variable) {
        super(`unresolved variable: \${${variable}}`);
        this.name = 'UnresolvedVariableError';
        this.variable = variable;
    }
}
exports.UnresolvedVariableError = UnresolvedVariableError;
class HookInitiateError extends Error {
    reason;
    constructor(reason, options) {
        super(`hook initiate failed: ${reason}`, options);
        this.name = 'HookInitiateError';
        this.reason = reason;
    }
}
exports.HookInitiateError = HookInitiateError;
const VARIABLE_PATTERN = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
function expand(template, vars) {
    return template.replace(VARIABLE_PATTERN, (_match, name) => {
        const val = vars[name];
        if (val === undefined)
            throw new UnresolvedVariableError(name);
        return val;
    });
}
async function executeHook(hook, ctx) {
    const vars = {
        apiEndpoint: ctx.apiEndpoint,
        returnUrl: ctx.returnUrl
    };
    const useResync = ctx.mode === 'resync' && hook.resync != null;
    const initiateStep = useResync ? hook.resync?.initiate : hook.initiate;
    const openStep = useResync ? hook.resync : hook.open;
    if (initiateStep != null) {
        await runInitiate(initiateStep, vars);
    }
    const expandedUrl = expand(openStep.url, vars);
    const finalUrl = appendParams(expandedUrl, openStep.params, vars);
    const allowedOrigin = new URL(finalUrl).origin;
    const isEmbedded = openStep.embed === true && ctx.embeddable;
    return {
        mode: isEmbedded ? 'iframe' : 'redirect',
        finalUrl,
        allowedOrigin
    };
}
// Best-effort: disconnect must always succeed locally, so errors are logged and swallowed.
async function executeDisconnect(hook, ctx) {
    const step = hook.disconnect;
    if (step == null)
        return;
    const vars = {
        apiEndpoint: ctx.apiEndpoint,
        returnUrl: ctx.returnUrl
    };
    try {
        const res = await runHttpStep(step, vars);
        if (!res.ok)
            logger.warn(`hookExecutor: disconnect HTTP ${res.status} (swallowed)`);
    }
    catch (err) {
        logger.warn('hookExecutor: disconnect error (swallowed):', err);
    }
}
async function runInitiate(step, vars) {
    let res;
    try {
        res = await runHttpStep(step, vars);
    }
    catch (err) {
        if (err instanceof UnresolvedVariableError)
            throw err;
        throw new HookInitiateError(`network: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
    }
    if (!res.ok) {
        throw new HookInitiateError(`HTTP ${res.status}`);
    }
    let json;
    try {
        json = await res.json();
    }
    catch (err) {
        throw new HookInitiateError(`invalid JSON: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
    }
    if (json == null || typeof json !== 'object') {
        throw new HookInitiateError('response is not an object');
    }
    const field = step.openUrlField ?? 'openUrl';
    const value = json[field];
    if (typeof value !== 'string' || value.length === 0) {
        throw new HookInitiateError(`response.${field} missing or not a string`);
    }
    vars.openUrl = value;
}
async function runHttpStep(step, vars) {
    const method = step.method ?? 'POST';
    const url = expand(step.url, vars);
    const headers = {};
    if (step.body != null)
        headers['content-type'] = 'application/json';
    if (step.auth != null)
        headers['Authorization'] = expand(step.auth, vars);
    const body = step.body != null ? JSON.stringify(expandBody(step.body, vars)) : undefined;
    return await fetch(url, { method, headers, body });
}
function expandBody(body, vars) {
    const out = {};
    for (const [k, v] of Object.entries(body)) {
        out[k] = typeof v === 'string' ? expand(v, vars) : v;
    }
    return out;
}
function appendParams(url, params, vars) {
    if (params == null || Object.keys(params).length === 0)
        return url;
    const parsed = new URL(url);
    for (const [k, v] of Object.entries(params)) {
        parsed.searchParams.set(k, expand(v, vars));
    }
    return parsed.toString();
}
